import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash2, Send, Video, Square, RotateCcw, Loader2, CheckCircle } from 'lucide-react';

const MAX_QUESTIONS = 5;
const MAX_PROMPT_SECONDS = 90;

const defaultQuestion = () => ({ prompt_text: '', max_seconds: 60, allow_retake: false, video_url: null });

// ────────────────────────────────────────────────────────────────────
// Inline per-question recorder (recruiter records short video prompts)
// ────────────────────────────────────────────────────────────────────
const PromptRecorder = ({ index, videoUrl, onUploaded, onClear }) => {
  const [stage, setStage] = useState('idle'); // idle | preparing | recording | uploading | error
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const cleanup = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    recorderRef.current = null;
    chunksRef.current = [];
  };

  const startRecording = async () => {
    setError(null);
    setStage('preparing');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      let mimeType = 'video/webm;codecs=vp9,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = '';
      chunksRef.current = [];
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
        cleanup();
        await upload(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setElapsed(0);
      setStage('recording');
      timerRef.current = setInterval(() => {
        setElapsed((s) => {
          if (s + 1 >= MAX_PROMPT_SECONDS) { stopRecording(); }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      setError('Could not access camera/mic. Please grant permission.');
      setStage('error');
      cleanup();
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setStage('uploading');
  };

  const upload = async (blob) => {
    try {
      const fd = new FormData();
      fd.append('file', blob, `prompt-q${index}.webm`);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/async-interviews/upload-prompt`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );
      if (res.data?.success) {
        onUploaded(res.data.data.url);
        setStage('idle');
      } else {
        setError(res.data?.message || 'Upload failed');
        setStage('error');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Upload failed');
      setStage('error');
    }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (videoUrl && stage === 'idle') {
    return (
      <div className="border border-purple-200 bg-purple-50 rounded-md p-2 flex items-center gap-2" data-testid={`ai-q-video-saved-${index}`}>
        <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0" />
        <video src={videoUrl} controls className="w-24 h-14 rounded bg-black object-cover" />
        <div className="flex-1 text-xs text-purple-800">Video prompt attached</div>
        <button
          type="button"
          onClick={() => { onClear(); }}
          className="text-xs text-red-600 hover:underline flex items-center gap-1"
          data-testid={`ai-q-video-remove-${index}`}
        >
          <Trash2 className="w-3 h-3" /> Remove
        </button>
        <button
          type="button"
          onClick={startRecording}
          className="text-xs text-purple-700 hover:underline flex items-center gap-1"
          data-testid={`ai-q-video-rerecord-${index}`}
        >
          <RotateCcw className="w-3 h-3" /> Re-record
        </button>
      </div>
    );
  }

  if (stage === 'recording' || stage === 'preparing') {
    return (
      <div className="border border-red-200 bg-red-50 rounded-md p-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-red-700">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            {stage === 'preparing' ? 'Starting…' : `Recording · ${fmt(elapsed)} / ${fmt(MAX_PROMPT_SECONDS)}`}
          </div>
          {stage === 'recording' && (
            <button
              type="button"
              onClick={stopRecording}
              className="text-xs bg-gray-900 text-white px-2 py-1 rounded inline-flex items-center gap-1"
              data-testid={`ai-q-video-stop-${index}`}
            >
              <Square className="w-3 h-3" /> Stop
            </button>
          )}
        </div>
        <video ref={videoRef} autoPlay muted playsInline className="w-full aspect-video bg-black rounded" />
      </div>
    );
  }

  if (stage === 'uploading') {
    return (
      <div className="border border-gray-200 rounded-md p-2 flex items-center gap-2 text-xs text-gray-600">
        <Loader2 className="w-3 h-3 animate-spin" /> Uploading video prompt…
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={startRecording}
        className="text-xs text-purple-600 hover:underline flex items-center gap-1"
        data-testid={`ai-q-video-record-${index}`}
      >
        <Video className="w-3 h-3" /> Record video prompt (optional)
      </button>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Main modal
// ────────────────────────────────────────────────────────────────────
const AsyncInterviewModal = ({ open, onOpenChange, candidate, recruiterId, onCreated }) => {
  const [title, setTitle] = useState(candidate?.appliedPosition ? `Video interview · ${candidate.appliedPosition}` : 'Video interview');
  const [questions, setQuestions] = useState([
    { prompt_text: 'Tell us briefly about your background and why you applied.', max_seconds: 60, allow_retake: true, video_url: null },
  ]);
  const [expiryMode, setExpiryMode] = useState('flex_3d');
  const [scheduledAt, setScheduledAt] = useState(() => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    d.setMinutes(0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const updateQuestion = (i, patch) => {
    setQuestions(prev => prev.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  };

  const addQuestion = () => {
    if (questions.length >= MAX_QUESTIONS) return;
    setQuestions(prev => [...prev, defaultQuestion()]);
  };

  const removeQuestion = (i) => {
    setQuestions(prev => prev.filter((_, idx) => idx !== i));
  };

  const validQuestions = questions
    .map(q => ({ ...q, prompt_text: q.prompt_text.trim() }))
    .filter(q => q.prompt_text);

  const canSubmit = title.trim() && validQuestions.length > 0 && !submitting && (expiryMode !== 'fixed' || scheduledAt);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        applicationId: candidate.applicationId,
        prospectId: candidate.prospectId || candidate.userId || candidate.id,
        positionId: candidate.appliedPositionId,
        recruiterId,
        title: title.trim(),
        questions: validQuestions,
        expiryMode,
        scheduledAt: expiryMode === 'fixed' ? new Date(scheduledAt).toISOString() : undefined,
      };
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/async-interviews`, payload);
      if (res.data?.success) {
        onCreated?.(res.data.data);
        onOpenChange(false);
      } else {
        setError(res.data?.message || 'Failed to create');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Server error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="async-interview-modal">
        <DialogHeader>
          <DialogTitle>Send Video Interview</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {candidate && (
            <p className="text-sm text-gray-600">
              Inviting <span className="font-medium text-gray-800">{candidate.firstName} {candidate.lastName}</span>
              {candidate.appliedPosition && <> for <span className="font-medium text-gray-800">{candidate.appliedPosition}</span></>}.
            </p>
          )}

          <div>
            <Label htmlFor="ai-title" className="text-sm">Title</Label>
            <Input
              id="ai-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1"
              data-testid="ai-title"
            />
          </div>

          {/* Questions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">Questions ({questions.length}/{MAX_QUESTIONS})</Label>
              {questions.length < MAX_QUESTIONS && (
                <button
                  type="button"
                  onClick={addQuestion}
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  data-testid="ai-add-question"
                >
                  <Plus className="w-3 h-3" /> Add question
                </button>
              )}
            </div>
            <div className="space-y-3">
              {questions.map((q, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`ai-question-${i}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-gray-400 mt-2">{i + 1}.</span>
                    <textarea
                      placeholder="e.g. Describe a project where you led a cross-functional team."
                      value={q.prompt_text}
                      onChange={(e) => updateQuestion(i, { prompt_text: e.target.value })}
                      className="flex-1 text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      rows={2}
                      data-testid={`ai-q-text-${i}`}
                    />
                    {questions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeQuestion(i)}
                        className="text-gray-400 hover:text-red-500 p-1 mt-1"
                        data-testid={`ai-q-remove-${i}`}
                        aria-label="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Recruiter video prompt (optional) */}
                  <div className="pl-4">
                    <PromptRecorder
                      index={i}
                      videoUrl={q.video_url}
                      onUploaded={(url) => updateQuestion(i, { video_url: url })}
                      onClear={() => updateQuestion(i, { video_url: null })}
                    />
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-600 pl-4">
                    <label className="flex items-center gap-1">
                      Max
                      <select
                        value={q.max_seconds}
                        onChange={(e) => updateQuestion(i, { max_seconds: parseInt(e.target.value, 10) })}
                        className="border border-gray-200 rounded px-2 py-0.5 text-xs"
                        data-testid={`ai-q-secs-${i}`}
                      >
                        <option value={30}>30s</option>
                        <option value={60}>60s</option>
                        <option value={90}>90s</option>
                        <option value={120}>120s</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={q.allow_retake}
                        onChange={(e) => updateQuestion(i, { allow_retake: e.target.checked })}
                        className="rounded"
                        data-testid={`ai-q-retake-${i}`}
                      />
                      Allow 1 retake
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Expiry */}
          <div>
            <Label className="text-sm">When can the candidate take this?</Label>
            <div className="mt-1 space-y-2">
              {[
                { key: 'flex_24h', label: 'Within 24 hours' },
                { key: 'flex_3d', label: 'Within 3 days' },
                { key: 'flex_7d', label: 'Within 7 days' },
                { key: 'fixed', label: 'At a specific time (auto-expires after 5 min)' },
              ].map(opt => (
                <label key={opt.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="expiry"
                    value={opt.key}
                    checked={expiryMode === opt.key}
                    onChange={() => setExpiryMode(opt.key)}
                    data-testid={`ai-expiry-${opt.key}`}
                  />
                  {opt.label}
                </label>
              ))}
              {expiryMode === 'fixed' && (
                <Input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="mt-2"
                  data-testid="ai-scheduled-at"
                />
              )}
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="ai-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="ai-send"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {submitting ? 'Sending…' : 'Send invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AsyncInterviewModal;
